import React, { useCallback, useContext, useEffect } from "react";
import { useClientBase } from "../../api/ClientBase";
import { useReportContext } from "../ReportContext";
import type { ReportChangeType } from "./ReportChangeType";
import type { Unsubscribe } from "./Unsubscribe";
import type {
  AccidentReportViewModel,
  DateTimeString,
} from "#accident-statement/schema";
import { useReportClient } from "../../api/ReportClient";
import type { ReportUpdate } from "./ReportUpdate";

export type UpdateSubscriberCallback = (
  report: AccidentReportViewModel,
) => void;

export interface ReportUpdateSubscriber {
  updateType: ReportChangeType;
  callback: UpdateSubscriberCallback;
}

export interface IReportUpdatesContext {
  onUpdate(subscriber: ReportUpdateSubscriber): Unsubscribe;
}

export function useOnUpdate(
  updateType: ReportChangeType,
  callback: UpdateSubscriberCallback,
) {
  const { onUpdate } = useReportUpdatesContext();
  useEffect(() => {
    const unsubscribe = onUpdate({ updateType, callback });

    return unsubscribe;
  }, [updateType, callback, onUpdate]);
}

export const ReportUpdatesContext =
  React.createContext<IReportUpdatesContext | null>(null);

export function useReportUpdatesContext() {
  const context = useContext(ReportUpdatesContext);
  if (!context) throw new Error("No ReportUpdatesContext found!");
  return context;
}

export function hasUnreadUpdates({
  items,
  drawingUpdated,
  from,
}: {
  items: ReportUpdate[];
  drawingUpdated?: DateTimeString;
  from: Date;
}) {
  return items.length || (!!drawingUpdated && new Date(drawingUpdated) > from);
}

export function useReportUpdatesClient() {
  const { get } = useReportClient();
  const { report, otherParty } = useReportContext();

  return useClientBase(
    useCallback(
      ({ from }: { from: Date }) =>
        get<{ items: ReportUpdate[]; drawingUpdated?: DateTimeString }>(
          `report/${report.id}/updates?after=${from.toISOString()}&updatedBy=${otherParty}`,
        ),
      [get, report.id, otherParty],
    ),
  );
}
